import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/api/auth'
import { useState } from 'react'
import {
  TextField,
  Button,
  Alert,
  Box,
  InputAdornment,
  Typography,
} from '@mui/material'
import { Email, CheckCircle } from '@mui/icons-material'

const createForgotPasswordSchema = (t: (key: string) => string) => z.object({
  email: z
    .string()
    .min(1, t('auth.validation.emailRequired'))
    .email(t('auth.validation.emailInvalid')),
})

export function ForgotPasswordForm() {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const forgotPasswordSchema = createForgotPasswordSchema(t)
  type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null)
      setIsLoading(true)
      await authApi.forgotPassword(data.email)
      setIsSuccess(true)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error.response?.data?.message || error.message || t('auth.forgotPasswordError')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CheckCircle
            sx={{
              fontSize: 64,
              color: 'success.main',
              animation: 'scaleIn 0.5s ease-out',
              '@keyframes scaleIn': {
                from: {
                  transform: 'scale(0)',
                  opacity: 0,
                },
                to: {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>
        <Typography variant="h5" component="h2" gutterBottom fontWeight={700}>
          {t('auth.forgotPasswordSuccessTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.forgotPasswordSuccessMessage')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.email')}
            type="email"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{
          mt: 1,
          py: 1.5,
        }}
      >
        {isLoading ? t('common.loading') : t('auth.sendResetLink')}
      </Button>
    </Box>
  )
}
